import { ReactNode } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { classNames } from 'support/helpers/generic/generic';

export type StepStatus = 'completed' | 'current' | 'upcoming' | 'disabled';

export type StepItem = {
  title: ReactNode;
  description: ReactNode;
  status: StepStatus;
  children: ReactNode;
};

type StepsProps = {
  steps: Array<StepItem>;
};

export const Steps = ({ steps }: StepsProps) => {
  return (
    <div className="flex flex-col gap-3">
      {steps.map((step, index) => (
        <Step step={step} index={index + 1} key={`${index}|${steps.length}`} />
      ))}
    </div>
  );
};

const Step = ({ step, index }: { step: StepItem; index: number }) => {
  const isDisabled = step.status === 'disabled';

  return (
    <Disclosure defaultOpen={step.status === 'current'}>
      {({ open }) => (
        <>
          <DisclosureButton
            disabled={isDisabled}
            className={classNames(
              'flex w-full items-center gap-4 rounded-md bg-white p-3 text-start',
              isDisabled && 'opacity-50',
            )}
          >
            <StepCircle status={step.status} index={index} />
            <div className="flex min-w-0 flex-1 flex-col items-start">
              <span className="text-sm font-semibold text-gray-700">{step.title}</span>
              <span className="text-sm text-gray-500">{step.description}</span>
            </div>
            {open ? (
              <ChevronDownIcon className="size-6 shrink-0 text-gray-400" />
            ) : (
              <ChevronRightIcon className="size-6 shrink-0 text-gray-400" />
            )}
          </DisclosureButton>
          <DisclosurePanel className="pl-[68px]">{step.children}</DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
};

const StepCircle = ({ status, index }: { status: StepStatus; index: number }) => {
  if (status === 'completed') {
    return (
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-green-100" aria-hidden="true">
        <CheckIcon className="size-6 text-green-600" />
      </span>
    );
  }

  return (
    <span
      className={classNames(
        'flex size-10 shrink-0 items-center justify-center rounded-full text-sm',
        status === 'current' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500',
      )}
      aria-hidden="true"
    >
      {index}
    </span>
  );
};
